import { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, updateDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Bell, Droplet, Receipt, HelpCircle, Check, X } from 'lucide-react';

function WaiterCallsPanel({ restaurantId }) {
  const [calls, setCalls] = useState([]);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!restaurantId) return;

    console.log('Setting up waiter calls listener for restaurant:', restaurantId);

    // Use simpler query without orderBy to avoid composite index requirement
    const callsQuery = query(
      collection(db, 'restaurants', restaurantId, 'waiterCalls'),
      where('status', 'in', ['pending', 'acknowledged'])
    );

    const unsubscribe = onSnapshot(
      callsQuery,
      (snapshot) => {
        console.log('Waiter calls snapshot received:', snapshot.size, 'documents');
        
        const callsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('Waiter calls data:', callsData);
        
        // Sort in memory by createdAt descending
        callsData.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
        
        // Play sound if new pending call
        if (callsData.some(call => call.status === 'pending')) {
          console.log('Playing notification sound for pending calls');
          playNotificationSound();
        }
        
        setCalls(callsData);
      },
      (error) => {
        console.error('Error listening to waiter calls:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        // Try to continue even if there's an error
      }
    );

    return () => unsubscribe();
  }, [restaurantId]);

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(err => console.log('Audio play failed:', err));
    }
  };

  const getIcon = (type) => {
    const icons = {
      waiter_call: Bell,
      water: Droplet,
      bill: Receipt,
      assistance: HelpCircle
    };
    return icons[type] || Bell;
  };

  const getColor = (type) => {
    const colors = {
      waiter_call: 'bg-blue-100 text-blue-600 border-blue-200',
      water: 'bg-cyan-100 text-cyan-600 border-cyan-200',
      bill: 'bg-green-100 text-green-600 border-green-200',
      assistance: 'bg-orange-100 text-orange-600 border-orange-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const acknowledgeCall = async (callId) => {
    try {
      const callRef = doc(db, 'restaurants', restaurantId, 'waiterCalls', callId);
      await updateDoc(callRef, {
        status: 'acknowledged',
        acknowledgedAt: new Date()
      });
    } catch (error) {
      console.error('Error acknowledging call:', error);
    }
  };

  const resolveCall = async (callId) => {
    try {
      const callRef = doc(db, 'restaurants', restaurantId, 'waiterCalls', callId);
      await updateDoc(callRef, {
        status: 'resolved',
        resolvedAt: new Date()
      });
    } catch (error) {
      console.error('Error resolving call:', error);
    }
  };

  const getTimeElapsed = (createdAt) => {
    if (!createdAt) return 'Just now';
    const now = new Date();
    const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const diff = Math.floor((now - created) / 1000); // seconds

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  if (calls.length === 0) {
    return null;
  }

  return (
    <>
      {/* Audio element for notification */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjGH0fPTgjMGHm7A7+OZTA0PVKzn77BdGAg+ltryxnMpBSh+zPLaizsIGGS57OihUBALTqXh8bllHAU2jdXz0H0vBSF1xe/glEILElyx6OyrWBUIQ5zd8sFuJAYugM/z24c5BxxpvO7mnEwNC1Kq5O+zYBoGPJPY88p2KwUme8rx3JBBChVasuruqVYUDEil4PK8cSMGM4rT89GBLwYeb8Hu45hKDANTrOfvtGEaBkCY3PLEdisFKH3M8tuNOgcZZ77s56FODwxPqOPxt2YdBjiP1/PMey0FI3fH792RSAwUXLPp7KlXFQlBm9/yuG8hBzCFz/PWhTYHHWu/7uWYSgwMU6vl77RgGgZAmNzyxHgrBSh9zPLajToHGGe97OahTg8LT6jj8bVlHQU3jdXzzn0uBSN3xu/fkkkKFFWw6uurWhYJP5vd8bpwIwcvg87y14Y2Bhxnvu3kmEoLDFOr5e+0YBsGP5fb8sR4KwUofczy2ow6BxdmvOznoU4PC0+o4/G1ZRwGNo3V889+LgUjdsXv35NJDBRV" />

      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg flex items-center space-x-2">
            <Bell className="w-5 h-5 text-red-500 animate-pulse" />
            <span>Staff Requests</span>
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
              {calls.filter(c => c.status === 'pending').length}
            </span>
          </h2>
        </div>

        <div className="space-y-3">
          {calls.map(call => {
            const Icon = getIcon(call.type);
            const colorClass = getColor(call.type);
            const isPending = call.status === 'pending';

            return (
              <div 
                key={call.id} 
                className={`border rounded-lg p-4 ${colorClass} ${isPending ? 'animate-pulse' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${isPending ? 'bg-white' : 'bg-opacity-50'}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Table {call.tableId}</p>
                      <p className="font-medium">{call.label}</p>
                      <p className="text-sm opacity-75 mt-1">
                        {getTimeElapsed(call.createdAt)}
                        {call.status === 'acknowledged' && ' • Acknowledged'}
                      </p>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    {isPending && (
                      <button
                        onClick={() => acknowledgeCall(call.id)}
                        className="p-2 bg-white rounded-lg hover:bg-opacity-80 transition-colors"
                        title="Acknowledge"
                      >
                        <Check className="w-5 h-5 text-green-600" />
                      </button>
                    )}
                    <button
                      onClick={() => resolveCall(call.id)}
                      className="p-2 bg-white rounded-lg hover:bg-opacity-80 transition-colors"
                      title="Mark as Done"
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default WaiterCallsPanel;
